import React, { useEffect, useState } from "react";
import CustomerInfoCard from "../components/CustomerInfoCard";
import ContactsCard from "../components/ContactsCard";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IDataService from "data/IDataService";
import IRepository from "data/IRepository";
import CustomerDTO from "../dtos/CustomerDTO";
import ContactDTO from "../dtos/ContactDTO";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import {
  Customer_Get,
  Customer_Contacts,
  Customer_SummaryInfo,
  Customer_ContactPerson,
} from "data/endpoints";
import { ContactType } from "../enums/contactType";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import ContactsDialog from "../components/ContactsDialog";
import { ContactGroupType } from "../enums/contactGroupType";
import SummaryInfoDTO from "../dtos/SummaryInfoDTO";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { appendQueryParams, getSummaryCountsParams } from "features/shared/helpers/queryParams";
import ContactPersonDTO from "features/contractualAccounts/dtos/ContactPersonDTO";
import { useQuery } from "data/hooks/useQuery";
import DataQuery from "data/DataQuery";
import ContactPersonInline from "features/contractualAccounts/components/ContactPersonInline";
import { useTranslation } from "react-i18next";
import { useStateDataGrid } from "features/shared/components/dataGrid/hooks/useStateDataGrid";
import Pagination from "features/shared/components/pagination/Pagination";
import ModalFooter from "features/shared/components/modal/ModalFooter";
import ContractsCountState from "infrastructure/store/contractsCount/ContractsCountState";

// number of show contacts
const contactsCount = 4;
// contacst dialog pagination
const contacstDialogCount = 30;

const CustomerSection = () => {
  // translations
  const tLabels = useTranslation("customer\\customer").t;
  const tTypes = useTranslation("customer\\enums\\contactType").t;

  // DI
  const dataService = useInjection<IDataService>(IocTypes.DataService);
  const customerRepo = useInjection<IRepository<CustomerDTO>>(IocTypes.Repository);
  const summaryInfoRepo = useInjection<IRepository<SummaryInfoDTO>>(IocTypes.Repository);
  const contactRepo = useInjection<IRepository<ContactDTO>>(IocTypes.Repository);

  // STORE
  const customer = useSelector<IAppState, CustomerState>( (store) => store.customer).customer;
  const contractsCount = useSelector<IAppState, ContractsCountState>((store) => store.contractsCount).contractsCount;

  // STATE
  const [customerDetail, setCustomerDetail] = useState<CustomerDTO>({});
  const [summaryInfo, setSummaryInfo] = useState<SummaryInfoDTO>({});
  const [emailContacst, setEmailContacst] = useState<PagedDataResultDTO<ContactDTO>>({ result: [], count: 0 });
  const [phoneContacst, setPhoneContacst] = useState<PagedDataResultDTO<ContactDTO>>({ result: [], count: 0 });
  const [dataBoxContacts, setDataBoxContacts] = useState<PagedDataResultDTO<ContactDTO>>({ result: [], count: 0 });
  // contacts dialog
  const [contactsDialogShow, setContactsDialogShow] = useState(false);
  const [modalContactTypes, setModalContactTypes] = useState<ContactType[]>([]);

  // DATA GRID HOOk
  const {
    dataGrid,
    paginationPageChangeHandler,
    filterResetHandler
  } = useStateDataGrid(contacstDialogCount);

  // side effects
  // fetch data for module (page)
  useEffect(() => {
    let routeParams = getRouteParams("customerId", customer.customerId!);

    // fetch customer defail info
    dataService.fetchAndSetOne<CustomerDTO>(
      customerRepo,
      setCustomerDetail,
      Customer_Get,
      routeParams,
      undefined,
      true
    );
    // fetch summary info for customer
    dataService.fetchAndSetOne<SummaryInfoDTO>(
      summaryInfoRepo,
      setSummaryInfo,
      Customer_SummaryInfo,
      routeParams,
      undefined,
      true
    );
    // fetch emails contacts
    dataService.fetchAndSetPagedResult<ContactDTO>(
      contactRepo,
      setEmailContacst,
      Customer_Contacts,
      routeParams,
      getContactParams(contactsCount, ContactType.Email),
      true
    );
    // fetch mobila/phone contacts
    dataService.fetchAndSetPagedResult<ContactDTO>(
      contactRepo,
      setPhoneContacst,
      Customer_Contacts,
      routeParams,
      getContactParams(contactsCount, ContactType.Mobile, ContactType.Phone),
      true
    );
    // fetch data-box contacts
    dataService.fetchAndSetPagedResult<ContactDTO>(
      contactRepo,
      setDataBoxContacts,
      Customer_Contacts,
      routeParams,
      getContactParams(contactsCount, ContactType.DataBox),
      true
    );
  }, [customer]);

  // fetch data for contacts dialog
  useEffect(() => {
    if (!contactsDialogShow) {
      filterResetHandler();
    }
  }, [contactsDialogShow]);

  // fetch contact person
  const contactPersounQuery: DataQuery = {
    url: Customer_ContactPerson,
    routeParams: getRouteParams("customerId", customer.customerId!)
  };
  const { data: contactPerson, loadStatus: contactPersonLoadStatus } = useQuery<ContactPersonDTO>(contactPersounQuery, {}, [customer]);

  // fetch data for contacts dialog
  const modalContacsQuery: DataQuery = {
    url: Customer_Contacts,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: appendQueryParams(new URLSearchParams(dataGrid.computedUrlSearchParams), "ContactType", modalContactTypes)
  };

  const { data: modalContacts, loadStatus: contractsStatus } = useQuery<
    PagedDataResultDTO<ContactDTO>
  >(
    modalContacsQuery,
    { count: 0, result: [] },
    [dataGrid.computedUrlSearchParams, customer.customerId, contactsDialogShow],
    { useTracker: true },
    { apply: true, condition: () => contactsDialogShow }
  );

  // helpers
  const getContactParams = (
    count: number,
    ...types: ContactType[]
  ): URLSearchParams => {
    let params = new URLSearchParams();
    params.set("PageSize", count.toString());

    for (const type of types) {
      params.append("ContactType", type);
    }

    return params;
  };

  // SUB-RENDERS
  const renderContactPerson = () => {
    if (contactPersonLoadStatus === "AfterFirstLoad" && contactPerson) {
      return (
        <ContactPersonInline 
          person={contactPerson} 
          title={tLabels("customerSection.representedBy") + ":"}
        /> 
      );
    }    
  }

  const renderContactDialogPagination = () => { 
    return (
      <ModalFooter className="justify-content-center">
        <Pagination
          urlParams={new URLSearchParams(dataGrid.computedUrlSearchParams)}
          count={modalContacts.count}
          pageSize={contacstDialogCount}
          onPageChange={paginationPageChangeHandler}
          />
      </ModalFooter>
    );
  }

  // RENDER
  return (
    <>
      <h4 className="text-muted">{tLabels("customerSection.customer")}</h4>
      <CustomerInfoCard
        customer={customerDetail}
        summaryInfo={summaryInfo}
        contractCounts={contractsCount}
      />
      <div className="d-flex flex-wrap align-items-center justify-content-between mt-4 mb-2">
        <h4 className="mr-3 mb-0 text-muted">
          {tLabels("customerSection.customerContacts")}
        </h4>
        {renderContactPerson()}
      </div>
      <div className="row no-gutters mx-n2">
        <div className="col-sm-6 col-lg-4 px-2">
          <ContactsCard
            title={tTypes(ContactType.Email)}
            contacts={emailContacst.result}
            count={emailContacst.count}
            showContactsCount={contactsCount}
            contactGroup={ContactGroupType.Email}
            setContactsDialogShow={setContactsDialogShow}
            setDialogContactTypes={setModalContactTypes}
          />
        </div>
        <div className="col-sm-6 col-lg-4 px-2">
          <ContactsCard
            title={`${tTypes(ContactType.Phone)}/${tTypes(ContactType.Mobile)}`}
            contacts={phoneContacst.result}
            count={phoneContacst.count}
            showContactsCount={contactsCount}
            contactGroup={ContactGroupType.Telephone}
            setContactsDialogShow={setContactsDialogShow}
            setDialogContactTypes={setModalContactTypes}
          />
        </div>
        <div className="col-sm-6 col-lg-4 px-2">
          <ContactsCard
            title={tTypes(ContactType.DataBox)}
            contacts={dataBoxContacts.result}
            count={dataBoxContacts.count}
            contactGroup={ContactGroupType.DataBox}
            setContactsDialogShow={setContactsDialogShow}
            setDialogContactTypes={setModalContactTypes}
          />
        </div>
      </div>
      <ContactsDialog
        show={contactsDialogShow}
        setShow={setContactsDialogShow}
        contacts={modalContacts.result}
        contactTypes={modalContactTypes}
        enableEditContact
        paginationRender={modalContacts.count > contacstDialogCount ? renderContactDialogPagination : undefined}
      />
    </>
  );
};

export default CustomerSection;
